import isEmpty from 'lodash/isEmpty';

import type {
  CheckoutFragment,
  CheckoutLine,
  CheckoutLineFragment,
  CountryCode,
  DropdownAttributeFragment,
  Maybe,
  Product,
} from '@/graphql/saleor/api';
import { getDropdownValue } from '@/lib/attributes';
import { getFilteredWristSizesByCountryAndProduct } from '@/lib/buckles';

export const getBraceletSizeOptions = (
  country: CountryCode,
  product: Product,
) => {
  return getFilteredWristSizesByCountryAndProduct(country, product);
};

export function hasMetadataKey(
  checkoutLine: CheckoutLine | undefined,
  key: string,
): boolean {
  return !!checkoutLine?.metadata.some((item) => item.key === key);
}

// Get warranty or strap or buckle product info for your watch or strap
export function getRelatedProductWithCheckoutLineId(
  checkout: CheckoutFragment['lines'] | undefined,
  checkoutLineId: string | undefined,
  type: string, // watch | strap
  relatedType: string, // warranty | strap | buckle
) {
  let relatedProductLineId: string | null = null;
  const relatedProduct = checkout?.find((checkoutLine) => {
    let condition = false;
    if (type === 'watch') {
      condition =
        checkoutLine.relatedProductWatchId === checkoutLineId &&
        checkoutLine.variant.product.productType.name.toLowerCase() ===
          relatedType;
    } else {
      condition =
        checkoutLine.relatedProductStrapId === checkoutLineId &&
        checkoutLine.variant.product.productType.name.toLowerCase() ===
          relatedType;
    }
    relatedProductLineId = condition ? checkoutLine.id : null;
    return condition;
  });

  return relatedProduct && relatedProductLineId
    ? {
        ...relatedProduct,
        checkoutLineId: relatedProductLineId,
      }
    : null;
}

export function getRelatedBuckle(
  checkout: CheckoutFragment['lines'] | undefined,
  strapCheckoutLine: CheckoutLineFragment,
) {
  const isStrap: boolean =
    strapCheckoutLine?.variant?.product.productType.name.toLowerCase() ===
    'strap';
  if (isStrap && !isEmpty(strapCheckoutLine.strapPackReference)) {
    return checkout?.find(
      (checkoutLine) =>
        checkoutLine.strapPackReference ===
          strapCheckoutLine?.strapPackReference &&
        checkoutLine.strapReference === strapCheckoutLine?.variant.sku,
    );
  } else {
    return checkout?.find(
      (checkoutLine) =>
        checkoutLine.id === strapCheckoutLine?.relatedProductBuckleId,
    );
  }
}

export interface LineWithVariant {
  id: string;
  variant?: {
    product: {
      warrantyExtensionDuration: Maybe<DropdownAttributeFragment>;
    };
  };
}

export function getWarrantyExtension<T extends LineWithVariant>(
  lines: T[] | undefined,
  relatedProductWatchId: string | undefined,
) {
  const watchProductLine = lines?.find(
    (line) => line.id === relatedProductWatchId,
  );
  return (
    watchProductLine &&
    getDropdownValue(
      watchProductLine?.variant?.product.warrantyExtensionDuration,
    )
  );
}
